import { Injectable } from '@angular/core';
import { EMPTY, interval, Observable, ReplaySubject } from 'rxjs';
import { fromFetch } from 'rxjs/fetch';
import { delay, filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MixpanelService } from './mixpanel.service';
import { SlSnackBarComponent } from '@sportlogiq/shared/sl-snack-bar/sl-snack-bar.component';
import { SlSnackBarType } from '@sportlogiq/shared/sl-snack-bar/sl-snack-bar.types';

const VERSION_CHECK_INTERVAL = 1000 * 60;

export interface VersionManifest {
    dirty: boolean;
    raw: string;
    hash: string;
    distance: null;
    tag: null;
    semver: null;
    suffix: string;
    semverString: null;
}

@Injectable()
export class VersionCheckService {
    private _loadedVersionManifest$ = new ReplaySubject<VersionManifest>();
    private _manifestFilePath = '/assets/manifest.json';
    private _snackbarOpened = false;

    constructor(
        private _snackBar: MatSnackBar,
        private _mixpanel: MixpanelService
    ) {}

    public init(): void {
        const getVersionManifest$: Observable<VersionManifest> = fromFetch(this._manifestFilePath, { credentials: 'include' }).pipe(
            switchMap(response => {
                if (response.ok) {
                    return response.json();
                }
                this._mixpanel.eventTrack('AppVersionUpdateManifestError', {
                    errorMessage: `Couldn't fetch the version manifest: ${response.statusText}`,
                });
                return EMPTY;
            })
        );

        getVersionManifest$.pipe(take(1)).subscribe(val => {
            this._loadedVersionManifest$.next(val);
        });

        // check manifest every minute
        interval(VERSION_CHECK_INTERVAL).pipe(
            filter(() => !this._snackbarOpened),
            switchMap(() => getVersionManifest$),
            withLatestFrom(this._loadedVersionManifest$),
            switchMap(([latest, current]) => {
                if (latest.hash !== current.hash) {
                    return this.showSnackbar();
                } else {
                    return EMPTY;
                }
            })
        ).subscribe(({ reloadApp }) => {
            this._snackbarOpened = false;
            if (reloadApp) {
                window.location.reload();
            }
        });
    }

    showSnackbar(): Observable<{ reloadApp: boolean; }> {
        const snackBarRef = this._snackBar.openFromComponent(SlSnackBarComponent, { duration: undefined });
        snackBarRef.instance.matSnackBarRef = snackBarRef;
        snackBarRef.instance.type = SlSnackBarType.WARNING;
        snackBarRef.instance.message = 'A new version of the application is available.';
        snackBarRef.instance.primaryActionLabel = 'Refresh now!';
        snackBarRef.instance.showDismissButton = true;
        this._snackbarOpened = true;

        const onRefreshClick$ = snackBarRef.afterDismissed().pipe(
            filter(({ dismissedByAction }) => dismissedByAction),
            map(() => {
                this._mixpanel.eventTrack('AppVersionUpdateReloadApp');
                return { reloadApp: true };
            }),
            delay(300) // give some time to mixpanel to send the event, not 100% guaranteed
        );

        this._mixpanel.eventTrack('AppVersionUpdateReceived');

        return onRefreshClick$.pipe(take(1));
    }
}
